import type { JSX } from 'react';


export type ResolvabilityClass = 'resolvable' | 'unresolvable';

export interface ResolvabilityOption {
  action: string;
  actor: 'client' | 'platform' | 'operator';
  consequence: string;
  trade_off: string;
}

export interface ResolvabilityConflict {
  what_failed: string;
  why_code_cannot_fix_it: string;
  limited_by: 'operating_system' | 'platform' | 'toolchain' | 'none';
  resolvable_by_code: boolean;
  evidence_file: string;
  evidence_line: number;
  options: ReadonlyArray<ResolvabilityOption>;
}

export interface ResolvabilityAssessment {
  failure_summary: string;
  attempted_remedies: string;
  conflicts: ReadonlyArray<ResolvabilityConflict>;
}

export interface ResolvabilityPayload {
  classification: ResolvabilityClass;
  assessment: ResolvabilityAssessment;
}

const WHO: Record<ResolvabilityOption['actor'], string> = {
  client: 'Your team',
  platform: 'This platform',
  operator: 'You'
};

const WHERE: Record<ResolvabilityConflict['limited_by'], string> = {
  operating_system: 'Limited by the operating system this platform runs on',
  platform: 'Limited by how this platform is set up',
  toolchain: 'Limited by the tools available on this machine',
  none: 'A problem in the code itself'
};

export function ResolvabilityWarning({
  resolvability
}: {
  resolvability: ResolvabilityPayload | undefined;
}): JSX.Element | null {
  const assessment = resolvability?.assessment;
  if (
    resolvability === undefined ||
    resolvability.classification !== 'unresolvable' ||
    assessment === undefined ||
    assessment.conflicts.length === 0
  ) {
    return null;
  }

  const blocking = assessment.conflicts.filter((c) => !c.resolvable_by_code);
  if (blocking.length === 0) return null;

  return (
    <section
      data-testid="resolvability-warning"
      className="mx-4 mb-3 rounded-lg border border-err/40 overflow-hidden"
    >
      <header className="px-3 py-2 flex items-center gap-2 border-b border-err/30 bg-err/10">
        <span className="text-[11px] px-2 py-0.5 rounded-[10px] font-medium bg-err text-white">
          MORE REWORK CANNOT FIX THIS
        </span>
        <span className="text-[13px] font-semibold">{assessment.failure_summary}</span>
      </header>

      <div className="px-3 py-3 flex flex-col gap-4">
        {assessment.attempted_remedies.length > 0 ? (
          <div className="text-[12px] opacity-70">
            <span className="font-medium">Already tried:</span> {assessment.attempted_remedies}
          </div>
        ) : null}

        {blocking.map((c, i) => (
          <div key={`${c.evidence_file}-${i}`} className="flex flex-col gap-2">
            <div className="px-3 py-2 rounded border-l-[3px] border-err bg-err/10">
              <div className="text-[12px] font-semibold text-err">{WHERE[c.limited_by]}</div>
              <div className="text-[13px] mt-1">
                <span className="font-medium">What failed:</span> {c.what_failed}
              </div>
              <div className="text-[13px] mt-0.5">
                <span className="font-medium">Why changing the code will not help:</span>{' '}
                {c.why_code_cannot_fix_it}
              </div>
            </div>

            <div className="text-[11px] opacity-60">
              Found in:{' '}
              <code className="font-mono select-all break-all opacity-90">
                {c.evidence_line > 0 ? `${c.evidence_file}:${c.evidence_line}` : c.evidence_file}
              </code>
            </div>

            {c.options.length > 0 ? (
              <div>
                <div className="text-[12px] font-medium">Ways forward</div>
                <ol className="mt-1 flex flex-col gap-2 list-decimal list-inside">
                  {c.options.map((o, j) => (
                    <li key={j} className="text-[12px]">
                      {o.action}
                      <div className="ml-4 mt-0.5 text-[11px] opacity-70">
                        <span className="font-medium">{WHO[o.actor]}</span> would do this ·{' '}
                        {o.consequence}
                      </div>
                      {o.trade_off.length > 0 ? (
                        <div className="ml-4 text-[11px] opacity-55">{o.trade_off}</div>
                      ) : null}
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </div>
        ))}

        <div className="text-[12px] px-3 py-2 rounded bg-err/10 text-err">
          If you continue, the run will keep working on this and use <strong>time</strong> and{' '}
          <strong>paid AI credits</strong> without being able to get past it.
        </div>
      </div>
    </section>
  );
}
